import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trophy, ChevronLeft, CheckCircle, BarChart, ChevronRight, Wand2, Loader2 } from 'lucide-react';
import api from '../api/axios';
import Swal from 'sweetalert2';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Results = () => {
  const navigate = useNavigate();
  
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [campaignDetails, setCampaignDetails] = useState({ campaign_title: '', candidates: [] });
  const [isLoading, setIsLoading] = useState(true);
  
  const [generatingIds, setGeneratingIds] = useState({});

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/results/campaigns');
      setCampaigns(res.data);
    } catch (err) {
      console.error("Error fetching results campaigns", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignClick = async (campaignId) => {
    setSelectedCampaignId(campaignId);
    setIsLoading(true);
    try {
      const res = await api.get(`/results/campaigns/${campaignId}/candidates`);
      setCampaignDetails(res.data);
    } catch (err) {
      console.error("Error fetching candidates", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async (sessionId) => {
    setGeneratingIds(prev => ({ ...prev, [sessionId]: true }));
    try {
      await api.post(`/results/${sessionId}/evaluate`);
      handleCampaignClick(selectedCampaignId); 
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to generate report',
        text: 'Check backend logs.',
        confirmButtonColor: '#4F46E5',
      });
    } finally {
      setGeneratingIds(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const filteredCampaigns = campaigns.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ==========================================
  // VIEW 2: RANKED CANDIDATE LIST
  // ==========================================
  if (selectedCampaignId) {
    return (
      <div className="flex-1 h-[calc(100vh-5rem)] overflow-y-auto bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans transition-colors duration-500 relative custom-scrollbar">
        <style>{customStyles}</style>

        {/* Ambient Glows */}
        <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed"></div>
        <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed" style={{ animationDelay: '2s' }}></div>

        <div className="px-6 lg:px-12 py-8 max-w-[1400px] mx-auto w-full relative z-10">
          <button 
            onClick={() => setSelectedCampaignId(null)} 
            className="flex items-center text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium mb-8 transition-colors w-max text-sm tracking-wide"
          >
            <ChevronLeft size={18} /> Back to Campaigns
          </button>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-1">{campaignDetails.campaign_title}</h1>
              <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold">Completed Interviews</p>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/20 px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 text-xs tracking-wide shadow-sm">
              <Trophy size={16}/> Leaderboard
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-20 text-slate-400 dark:text-slate-500 font-light tracking-wide text-sm">Loading candidates...</div>
          ) : campaignDetails.candidates.length === 0 ? (
            <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-10 text-center rounded-[2rem] border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 font-light tracking-wide">
              No completed interviews found for this campaign yet.
            </div>
          ) : (
            <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
              <table className="w-full text-left min-w-[900px]">
                <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
                  <tr>
                    <th className="px-6 py-5 font-semibold">Rank</th>
                    <th className="px-6 py-5 font-semibold">Candidate</th>
                    <th className="px-6 py-5 font-semibold text-center">Overall Score</th>
                    <th className="px-6 py-5 font-semibold text-center">Skills Breakdown</th>
                    <th className="px-6 py-5 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {campaignDetails.candidates.map((cand, idx) => {
                    const isEvaluating = generatingIds[cand.session_id];

                    return (
                      <tr key={cand.session_id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                        <td className="px-6 py-6 font-bold text-slate-400 dark:text-slate-500">
                          {cand.is_evaluated ? `#${idx + 1}` : '-'}
                        </td>
                        <td className="px-6 py-6">
                          <p className="font-medium dark:font-light tracking-wide text-slate-900 dark:text-white text-base mb-1">{cand.candidate_name}</p>
                          <p className="text-[12px] text-slate-500 dark:text-slate-400 font-mono tracking-wide">{cand.candidate_email}</p>
                        </td>
                        
                        {cand.is_evaluated ? (
                          <>
                            <td className="px-6 py-6 text-center">
                              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-2 font-bold text-lg ${
                                cand.overall_score >= 8 ? 'border-emerald-500 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10' : 
                                cand.overall_score >= 5 ? 'border-amber-400 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10' : 'border-rose-400 text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10'
                              }`}>
                                {cand.overall_score}
                              </div>
                            </td>
                            <td className="px-6 py-6">
                              <div className="flex gap-6 justify-center text-[12px] tracking-wide">
                                <div className="space-y-1 text-slate-500 dark:text-slate-400">
                                  <div className="flex justify-between gap-4"><span>{cand.skill1_name}</span> <span className="font-bold text-slate-700 dark:text-white">{cand.skill1_score}/10</span></div>
                                  <div className="flex justify-between gap-4"><span>{cand.skill2_name}</span> <span className="font-bold text-slate-700 dark:text-white">{cand.skill2_score}/10</span></div>
                                  <div className="flex justify-between gap-4"><span>{cand.skill3_name}</span> <span className="font-bold text-slate-700 dark:text-white">{cand.skill3_score}/10</span></div>
                                </div>
                                <div className="border-l pl-6 border-slate-200 dark:border-white/10 space-y-1 text-slate-500 dark:text-slate-400">
                                  <div className="flex justify-between gap-4"><span>Comm.</span> <span className="font-bold text-slate-700 dark:text-white">{cand.communication_score}/10</span></div>
                                  <div className="flex justify-between gap-4"><span>Logic</span> <span className="font-bold text-slate-700 dark:text-white">{cand.problem_solving_score}/10</span></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-6 text-right">
                              <button 
                                onClick={() => navigate(`/report/${cand.session_id}`)}
                                className="bg-slate-100 dark:bg-white/5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 border border-transparent dark:border-white/10 px-5 py-2.5 rounded-xl text-xs font-bold tracking-wide flex items-center justify-end gap-2 ml-auto transition-all shadow-sm dark:shadow-none"
                              >
                                View Report <ChevronRight size={16}/>
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-6 text-center text-slate-400 dark:text-slate-500 italic font-light tracking-wide text-sm" colSpan="2">
                              Awaiting AI Evaluation
                            </td>
                            <td className="px-6 py-6 text-right">
                              <button 
                                onClick={() => handleGenerateReport(cand.session_id)}
                                disabled={isEvaluating}
                                className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-5 py-2.5 rounded-xl text-xs font-bold tracking-wide flex items-center justify-end gap-2 ml-auto transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] disabled:opacity-50 active:scale-95"
                              >
                                {isEvaluating ? <Loader2 size={16} className="animate-spin"/> : <Wand2 size={16}/>}
                                {isEvaluating ? "Analyzing..." : "Generate AI Report"}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW 1: CAMPAIGN LIST
  // ==========================================
  return (
    <div className="flex-1 h-[calc(100vh-5rem)] overflow-y-auto bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans transition-colors duration-500 relative custom-scrollbar">
      <style>{customStyles}</style>

      {/* Ambient Glows */}
      <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed"></div>
      <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed" style={{ animationDelay: '2s' }}></div>

      <div className="px-6 lg:px-12 py-8 max-w-[1400px] mx-auto w-full relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 border-b border-slate-200 dark:border-white/10 pb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-1">Interview Results</h1>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold">Select a campaign to view ranked candidates.</p>
          </div>
          
          <div className="relative w-full md:w-72 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors" size={16}/>
            <input 
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-sm placeholder:text-[12px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-20 text-slate-400 dark:text-slate-500 font-light tracking-wide text-sm">Loading campaigns...</div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-10 text-center rounded-[2rem] border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 font-light tracking-wide">
            No campaigns found matching your search.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((camp) => (
              <div 
                key={camp.id} 
                onClick={() => handleCampaignClick(camp.id)}
                className="bg-white dark:bg-white/5 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/15 hover:bg-slate-50 dark:hover:bg-white/10 shadow-sm dark:shadow-none cursor-pointer transition-all duration-300 p-8 flex flex-col group"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium dark:font-medium tracking-wide text-xl text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors pr-4">{camp.title}</h3>
                  <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide ${camp.status === 'completed' ? 'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-400' : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'}`}>
                    {camp.status}
                  </span>
                </div>
                
                <div className="text-[12px] text-slate-500 dark:text-slate-400 mb-8 px-2.5 py-1 bg-slate-50 dark:bg-black/40 rounded-md w-max border border-slate-100 dark:border-white/5 font-bold tracking-wide">
                  {camp.interview_type} Interview
                </div>

                <div className="mt-auto grid grid-cols-2 gap-4 pt-6 border-t border-slate-100 dark:border-white/10">
                  <div>
                    <p className="text-[12px] tracking-wide font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1.5"><CheckCircle size={12}/> Completed</p>
                    <p className="font-medium dark:font-light text-xl text-slate-900 dark:text-white">{camp.completed_count} <span className="text-sm text-slate-400">/ {camp.invited_count}</span></p>
                  </div>
                  <div>
                    <p className="text-[12px] tracking-wide font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1.5"><BarChart size={12}/> Avg Score</p>
                    <p className={`font-medium dark:font-light text-xl ${camp.avg_score >= 7 ? 'text-emerald-600 dark:text-emerald-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
                      {camp.avg_score > 0 ? `${camp.avg_score} ` : 'N/A'}
                      {camp.avg_score > 0 && <span className="text-sm text-slate-400 dark:text-slate-500">/ 10</span>}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;